import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { SchedulingSlot } from "../widget/state";

type SlotGroup = {
  dateKey: string;
  sortKey: number;
  label: string;
  sublabel: string;
  slots: SchedulingSlot[];
};

const startOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getDateKey = (iso: string) => {
  const date = new Date(iso);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const formatWeekday = (date: Date) =>
  new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(date);

const formatDateGroup = (iso: string) => {
  const date = new Date(iso);
  const today = startOfLocalDay(new Date());
  const slotDay = startOfLocalDay(date);
  const diffDays = Math.round(
    (slotDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays === 0) return { label: "Today", sublabel: formatWeekday(date) };
  if (diffDays === 1) return { label: "Tomorrow", sublabel: formatWeekday(date) };

  return {
    label: formatWeekday(date),
    sublabel: new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
    }).format(date),
  };
};

const formatTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const formatFullDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const groupSlotsByDate = (slots: SchedulingSlot[]): SlotGroup[] => {
  const groups = new Map<string, SlotGroup>();

  for (const slot of slots) {
    if (!slot.start_time) continue;
    const dateKey = getDateKey(slot.start_time);
    const existing = groups.get(dateKey);

    if (existing) {
      existing.slots.push(slot);
      continue;
    }

    const { label, sublabel } = formatDateGroup(slot.start_time);
    groups.set(dateKey, {
      dateKey,
      sortKey: startOfLocalDay(new Date(slot.start_time)).getTime(),
      label,
      sublabel,
      slots: [slot],
    });
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      slots: [...group.slots].sort(
        (a, b) =>
          new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
      ),
    }))
    .sort((a, b) => a.sortKey - b.sortKey);
};

@customElement("cg-scheduling-slots")
export class CGSchedulingSlots extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      margin-top: 10px;
      box-sizing: border-box;
      font-family: var(--cg-font);
      --sl-font-sans: var(--cg-font);
    }

    *,
    *::before,
    *::after {
      font-family: var(--cg-font);
    }

    button,
    input,
    textarea,
    select {
      font: inherit;
    }

    .widget {
      box-sizing: border-box;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      overflow: hidden;
      border: 1px solid var(--cg-border);
      border-radius: 12px;
      background: var(--cg-surface);
      color: var(--cg-text);
    }

    .header {
      box-sizing: border-box;
      display: flex;
      gap: 12px;
      align-items: flex-start;
      padding: 14px;
      border-bottom: 1px solid var(--cg-border);
    }

    .icon {
      width: 40px;
      height: 40px;
      flex: 0 0 auto;
      display: grid;
      place-items: center;
      border-radius: 10px;
      background: color-mix(in srgb, var(--cg-primary) 10%, transparent);
      color: var(--cg-primary);
    }

    .title {
      margin: 0;
      font-size: 14px;
      line-height: 1.3;
      font-weight: 600;
      color: var(--cg-text);
    }

    .subtitle {
      margin: 4px 0 0;
      font-size: 12px;
      line-height: 1.45;
      color: var(--cg-muted);
    }

    .error {
      border-bottom: 1px solid #fecaca;
      background: #fef2f2;
      color: #b91c1c;
      font-size: 12px;
      line-height: 1.5;
      padding: 10px 14px;
    }

    .body {
      box-sizing: border-box;
      padding: 14px;
    }

    .date-wrap {
      position: relative;
      min-width: 0;
    }

    .dates {
      display: flex;
      gap: 8px;
      overflow-x: auto;
      padding-bottom: 4px;
      scrollbar-width: none;
      scroll-behavior: smooth;
      min-width: 0;
    }

    .dates.with-arrows {
      margin-inline: 34px;
    }

    .dates::-webkit-scrollbar {
      display: none;
    }

    .date {
      min-width: 78px;
      border: 1px solid var(--cg-border);
      border-radius: 10px;
      background: var(--cg-bg);
      color: var(--cg-muted);
      text-align: left;
      padding: 9px 10px;
      cursor: pointer;
      transition:
        border-color 150ms ease,
        background 150ms ease,
        color 150ms ease;
    }

    .date:hover {
      border-color: color-mix(in srgb, var(--cg-primary) 45%, var(--cg-border));
      background: color-mix(in srgb, var(--cg-primary) 6%, var(--cg-bg));
    }

    .date.active {
      border-color: var(--cg-primary);
      background: var(--cg-primary);
      color: var(--cg-on-primary);
    }

    .date-label {
      display: block;
      font-size: 11px;
      line-height: 1.2;
      font-weight: 500;
      opacity: 0.85;
    }

    .date-sub {
      display: block;
      margin-top: 4px;
      font-size: 14px;
      line-height: 1.1;
      font-weight: 600;
    }

    .arrow {
      position: absolute;
      top: 50%;
      z-index: 2;
      width: 28px;
      height: 28px;
      display: grid;
      place-items: center;
      border: 1px solid var(--cg-border);
      border-radius: 999px;
      background: var(--cg-surface);
      color: var(--cg-muted);
      cursor: pointer;
      transform: translateY(-50%);
    }

    .arrow.left {
      left: 0;
    }

    .arrow.right {
      right: 0;
    }

    .time-head {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: 14px 0 8px;
      color: var(--cg-muted);
      font-size: 12px;
      font-weight: 500;
    }

    .times {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(72px, 1fr));
      gap: 8px;
      max-height: 160px;
      overflow-y: auto;
    }

    .time {
      border: 1px solid var(--cg-border);
      border-radius: 10px;
      background: var(--cg-bg);
      color: var(--cg-text);
      font-size: 13px;
      font-weight: 500;
      padding: 10px 6px;
      cursor: pointer;
      transition:
        border-color 150ms ease,
        background 150ms ease,
        color 150ms ease;
    }

    .time:hover {
      border-color: color-mix(in srgb, var(--cg-primary) 45%, var(--cg-border));
      background: color-mix(in srgb, var(--cg-primary) 6%, var(--cg-bg));
    }

    .time.active {
      border-color: var(--cg-primary);
      background: color-mix(in srgb, var(--cg-primary) 10%, var(--cg-bg));
      color: var(--cg-primary);
    }

    button:disabled {
      cursor: not-allowed;
      opacity: 0.62;
    }

    .confirmation-panel {
      box-sizing: border-box;
      margin-top: 14px;
      border: 1px solid var(--cg-border);
      border-radius: 14px;
      background: color-mix(in srgb, var(--cg-primary) 4%, var(--cg-surface));
      color: var(--cg-text);
      padding: 14px;
    }

    .confirmation-title {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
    }

    .confirmation-description {
      margin: 5px 0 14px;
      color: var(--cg-muted);
      font-size: 13px;
      line-height: 1.5;
    }

    label {
      display: block;
      margin-bottom: 10px;
    }

    .label-text {
      display: block;
      margin-bottom: 5px;
      color: var(--cg-muted);
      font-size: 12px;
      font-weight: 500;
    }

    input {
      width: 100%;
      box-sizing: border-box;
      border: 1px solid var(--cg-border);
      border-radius: 10px;
      background: var(--cg-bg);
      color: var(--cg-text);
      font-size: 13px;
      padding: 10px 11px;
      outline: none;
    }

    input:focus {
      border-color: var(--cg-primary);
    }

    .confirmation-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 14px;
    }

    .secondary,
    .primary {
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      padding: 9px 12px;
      cursor: pointer;
    }

    .secondary {
      border: 1px solid var(--cg-border);
      background: var(--cg-surface);
      color: var(--cg-text);
    }

    .primary {
      border: none;
      background: var(--cg-primary);
      color: var(--cg-on-primary);
    }
  `;

  @property({ attribute: false }) slots: SchedulingSlot[] = [];
  @property({ type: String }) provider = "";
  @property({ type: String }) bookingStatus:
    | "idle"
    | "booking"
    | "confirmed"
    | "error" = "idle";
  @property({ type: String }) bookingError = "";

  @state() private selectedDateKey = "";
  @state() private selectedSlotKey: string | null = null;
  @state() private pendingSlot: SchedulingSlot | null = null;
  @state() private confirmOpen = false;
  @state() private name = "";
  @state() private email = "";

  private get groups(): SlotGroup[] {
    return groupSlotsByDate(this.slots);
  }

  private get activeGroup(): SlotGroup | undefined {
    const groups = this.groups;
    return (
      groups.find((group) => group.dateKey === this.selectedDateKey) ??
      groups[0]
    );
  }

  override updated(): void {
    const groups = this.groups;
    if (!groups.length) return;

    const stillValid = groups.some(
      (group) => group.dateKey === this.selectedDateKey,
    );
    if (!stillValid && groups[0]) {
      this.selectedDateKey = groups[0].dateKey;
      this.selectedSlotKey = null;
    }
  }

  private scrollDates(direction: "left" | "right"): void {
    const dateStrip = this.renderRoot.querySelector<HTMLDivElement>(".dates");
    if (!dateStrip) return;
    dateStrip.scrollBy({
      left:
        direction === "left"
          ? -dateStrip.clientWidth * 0.7
          : dateStrip.clientWidth * 0.7,
      behavior: "smooth",
    });
  }

  private selectSlot(slot: SchedulingSlot, slotKey: string): void {
    if (this.bookingStatus === "booking" || this.bookingStatus === "confirmed") {
      return;
    }
    this.selectedSlotKey = slotKey;
    this.pendingSlot = slot;
    this.confirmOpen = true;

    this.updateComplete.then(() => {
      const panel = this.renderRoot.querySelector<HTMLElement>(".confirmation-panel");
      if (panel) {
        panel.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      this.dispatchEvent(new CustomEvent("slot-selected", { bubbles: true, composed: true }));
    });
  }

  private submit(): void {
    if (!this.pendingSlot || !this.name.trim() || !this.email.trim()) return;
    this.dispatchEvent(
      new CustomEvent("confirm-booking", {
        detail: {
          slot: this.pendingSlot,
          attendee: { name: this.name.trim(), email: this.email.trim() },
        },
        bubbles: true,
        composed: true,
      }),
    );
    this.confirmOpen = false;
  }

  private renderCalendarIcon() {
    return html`<svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect width="18" height="18" x="3" y="4" rx="2" />
      <path d="M3 10h18" />
    </svg>`;
  }

  private renderClockIcon() {
    return html`<svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>`;
  }

  override render() {
    if (!this.slots.length || this.bookingStatus === "confirmed") {
      return nothing;
    }

    const groups = this.groups;
    const activeGroup = this.activeGroup;
    if (!activeGroup) return nothing;

    const isDisabled = this.bookingStatus === "booking";
    const showArrows = groups.length > 2 && !isDisabled;
    const providerName =
      this.provider === "CALENDLY"
        ? "Calendly"
        : this.provider === "CALCOM"
          ? "Cal.com"
          : "";

    return html`
      <div class="widget">
        <div class="header">
          <div class="icon">${this.renderCalendarIcon()}</div>
          <div>
            <p class="title">Choose a meeting time</p>
            <p class="subtitle">
              ${this.bookingStatus === "booking"
                ? "Confirming your selected slot..."
                : providerName
                  ? `Available times from ${providerName}.`
                  : "Pick a date and time that works for you."}
            </p>
          </div>
        </div>

        ${this.bookingStatus === "error" && this.bookingError
          ? html`<div class="error">${this.bookingError}</div>`
          : nothing}

        <div class="body">
          <div class="date-wrap">
            ${showArrows
              ? html`
                  <button
                    type="button"
                    class="arrow left"
                    aria-label="Scroll dates left"
                    @click=${() => this.scrollDates("left")}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    class="arrow right"
                    aria-label="Scroll dates right"
                    @click=${() => this.scrollDates("right")}
                  >
                    ›
                  </button>
                `
              : nothing}
            <div
              class="dates ${showArrows ? "with-arrows" : ""}"
            >
              ${groups.map(
                (group) => html`
                  <button
                    type="button"
                    class="date ${group.dateKey === activeGroup.dateKey
                      ? "active"
                      : ""}"
                    ?disabled=${isDisabled}
                    @click=${() => {
                      this.selectedDateKey = group.dateKey;
                      this.selectedSlotKey = null;
                      this.pendingSlot = null;
                      this.confirmOpen = false;
                    }}
                  >
                    <span class="date-label">${group.label}</span>
                    <span class="date-sub">${group.sublabel}</span>
                  </button>
                `,
              )}
            </div>
          </div>

          <div class="time-head">
            ${this.renderClockIcon()}
            <span>
              ${activeGroup.label === "Today" ||
              activeGroup.label === "Tomorrow"
                ? activeGroup.label
                : `${activeGroup.label}, ${activeGroup.sublabel}`}
            </span>
          </div>

          <div class="times">
            ${activeGroup.slots.map((slot, index) => {
              const slotKey = `${slot.start_time}-${index}`;
              return html`
                <button
                  type="button"
                  class="time ${this.selectedSlotKey === slotKey ? "active" : ""}"
                  ?disabled=${isDisabled}
                  @click=${() => this.selectSlot(slot, slotKey)}
                >
                  ${this.bookingStatus === "booking" &&
                  this.selectedSlotKey === slotKey
                    ? "..."
                    : formatTime(slot.start_time)}
                </button>
              `;
            })}
          </div>

          ${this.confirmOpen && this.pendingSlot
            ? html`
                <div
                  class="confirmation-panel"
                  role="group"
                  aria-label="Confirm booking"
                >
                  <p class="confirmation-title">Confirm booking</p>
                  <p class="confirmation-description">
                    ${formatFullDateTime(this.pendingSlot.start_time)}
                  </p>

                  <label>
                    <span class="label-text">Name</span>
                    <input
                      type="text"
                      placeholder="Your name"
                      .value=${this.name}
                      ?disabled=${this.bookingStatus === "booking"}
                      @input=${(event: Event) => {
                        this.name = (event.target as HTMLInputElement).value;
                      }}
                    />
                  </label>

                  <label>
                    <span class="label-text">Email</span>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      .value=${this.email}
                      ?disabled=${this.bookingStatus === "booking"}
                      @input=${(event: Event) => {
                        this.email = (event.target as HTMLInputElement).value;
                      }}
                    />
                  </label>

                  <div class="confirmation-actions">
                    <button
                      type="button"
                      class="secondary"
                      ?disabled=${this.bookingStatus === "booking"}
                      @click=${() => {
                        this.confirmOpen = false;
                        this.pendingSlot = null;
                        this.selectedSlotKey = null;
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      class="primary"
                      ?disabled=${this.bookingStatus === "booking" ||
                      !this.name.trim() ||
                      !this.email.trim()}
                      @click=${() => this.submit()}
                    >
                      ${this.bookingStatus === "booking"
                        ? "Confirming..."
                        : "Confirm booking"}
                    </button>
                  </div>
                </div>
              `
            : nothing}
        </div>
      </div>
    `;
  }
}
